import { useCallback, useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { IconButton, Icon, Tooltip, icon as iconSize } from '@nula/core-ui';
import { isSectionHead, sectionRangeAt } from './section-scope';
import styles from './editor-chart-gutter.module.css';

export interface EditorChartGutterProps {
  editor: Editor;
  onGenerateChart: (range: { from: number; to: number }) => void;
  /** The editor's scroll container. When it scrolls, the rail/✨ is hidden
   *  (the fixed-position overlay would otherwise float at a stale spot). */
  scrollElement?: HTMLElement | null;
}

/**
 * Resolves the nearest ancestor node position that is a section head,
 * given viewport coordinates. Returns null when the cursor is not over
 * a section head.
 *
 * Exported for unit testing.
 */
export function sectionHeadPosAtCoords(
  editor: Editor,
  clientX: number,
  clientY: number,
): number | null {
  try {
    const posInfo = editor.view.posAtCoords({ left: clientX, top: clientY });
    if (!posInfo) return null;

    const { doc } = editor.state;
    const $pos = doc.resolve(Math.min(Math.max(posInfo.pos, 0), doc.content.size));

    // 1. If the hovered position is inside a section head itself (a heading, or
    //    a parent bullet), use it directly — walk ancestors innermost-first.
    for (let d = $pos.depth; d >= 1; d--) {
      const ancPos = $pos.before(d);
      if (isSectionHead(editor, ancPos)) return ancPos;
    }

    // 2. Otherwise the cursor is over BODY content. Resolve it to the section it
    //    belongs to: the nearest PRECEDING top-level heading. Scan top-level
    //    blocks up to the hovered block and keep the last heading seen.
    const blockPos = $pos.before(1);
    let nearestHeading: number | null = null;
    doc.forEach((node, offset) => {
      if (offset > blockPos) return;
      if (node.type.name === 'heading') nearestHeading = offset;
    });
    return nearestHeading;
  } catch {
    return null;
  }
}

export interface ActiveBand {
  pos: number;
  top: number;
  bottom: number;
}

/**
 * Sticky activation decision. Once a section is active, it STAYS active while
 * the cursor's Y is anywhere within its vertical band — even if the cursor is
 * off to the side (margin, resolves to null) or a neighbouring section briefly
 * resolves (boundary jitter). This kills the per-move flicker and the
 * reach-the-button vanish. It only switches when the cursor's Y is OUTSIDE the
 * active band: then it adopts the freshly hovered section, or clears if none.
 *
 * Returns the section pos that should now be active, or null to clear.
 * Exported for unit testing.
 */
export function nextActiveSection(args: {
  hoveredPos: number | null;
  cursorY: number;
  active: ActiveBand | null;
}): number | null {
  const { hoveredPos, cursorY, active } = args;
  // Sticky: still within the active section's vertical band → keep it.
  if (active && cursorY >= active.top && cursorY <= active.bottom) {
    return active.pos;
  }
  // Outside the active band: adopt the hovered section, or clear.
  return hoveredPos;
}

export function EditorChartGutter({ editor, onGenerateChart, scrollElement }: EditorChartGutterProps) {
  const posRef = useRef<number | null>(null);
  // The active section's vertical band — drives sticky activation (stay active
  // while the cursor's Y is within it, ignoring margin/jitter).
  const activeBandRef = useRef<ActiveBand | null>(null);
  // True while the pointer is over the ✨ button itself. The button sits at the
  // band's TOP edge, so reaching it (moving up) can momentarily resolve to null
  // ABOVE the band — being over the button is a first-class "stay alive"
  // condition that overrides that.
  const overButtonRef = useRef(false);
  // One-tick defer for the scroll-container mouseleave, so a button-enter that
  // follows it (leave→enter race) can abort the clear.
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelLeaveTimer = useCallback(() => {
    if (leaveTimerRef.current !== null) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  }, []);
  // Button position + the rail's full-section height. Rendered as fixed,
  // hover-transient elements (recomputed each hover, hidden on scroll). The rail
  // is a SINGLE element spanning the whole section (continuous gradient),
  // pointer-events:none so it can't intercept the drag handle.
  const [gutterPos, setGutterPos] = useState<{ top: number; left: number; height: number } | null>(null);

  const clearGutter = useCallback(() => {
    posRef.current = null;
    activeBandRef.current = null;
    overButtonRef.current = false;
    setGutterPos(null);
  }, []);

  // Attach mousemove / mouseleave to the SCROLL CONTAINER, not .ProseMirror —
  // the rail/✨ live in the left margin OUTSIDE .ProseMirror, so listening on
  // .ProseMirror fired mouseleave (→ hide) the moment the cursor entered the
  // margin to reach the button. The scroll container includes that margin.
  useEffect(() => {
    const dom =
      scrollElement ??
      editor.view.dom.closest<HTMLElement>('[data-scroll-container="editor"]') ??
      editor.view.dom;

    const onMouseMove = (e: MouseEvent) => {
      // A move back into the editor cancels a pending button/container leave.
      cancelLeaveTimer();
      const hoveredPos = sectionHeadPosAtCoords(editor, e.clientX, e.clientY);
      // Sticky decision: keep the active section while the cursor's Y is within
      // its band (ignores margin travel + boundary jitter → no flicker, no
      // vanish while reaching the button); otherwise adopt the hovered one.
      const next = nextActiveSection({
        hoveredPos,
        cursorY: e.clientY,
        active: activeBandRef.current,
      });

      if (next === null) {
        // Off any section and outside the active band → hide, UNLESS the pointer
        // is over the button itself (it sits at the band's top edge).
        if (posRef.current !== null && !overButtonRef.current) clearGutter();
        return;
      }

      // Already active → nothing to recompute. (posRef is set only AFTER a
      // successful coords measurement below, so a non-null posRef always implies
      // gutterPos is set — no stranded state.)
      if (next === posRef.current) return;

      // range.to is exclusive (next block start) → true bottom is the last
      // position inside. coordsAtPos is viewport space (position:fixed) and can
      // throw if the doc changed mid-handler — degrade to hiding.
      try {
        const range = sectionRangeAt(editor, next);
        if (!range) return;
        const topCoords = editor.view.coordsAtPos(range.from);
        const bottomCoords = editor.view.coordsAtPos(Math.max(range.from, range.to - 1));
        posRef.current = next;
        activeBandRef.current = { pos: next, top: topCoords.top, bottom: bottomCoords.bottom };
        setGutterPos({
          top: topCoords.top,
          left: topCoords.left,
          height: bottomCoords.bottom - topCoords.top,
        });
      } catch {
        clearGutter();
      }
    };

    // Leaving the scroll container fires even as the pointer crosses onto the
    // fixed button (which is rendered outside the container). The leave arrives
    // BEFORE the button's mouseenter, so we DEFER the clear one tick: if the
    // pointer landed on the button, mouseenter sets overButtonRef first and the
    // deferred clear aborts. Otherwise it hides.
    const onMouseLeave = () => {
      cancelLeaveTimer();
      leaveTimerRef.current = setTimeout(() => {
        if (!overButtonRef.current) clearGutter();
      }, 0);
    };

    dom.addEventListener('mousemove', onMouseMove);
    dom.addEventListener('mouseleave', onMouseLeave);
    return () => {
      dom.removeEventListener('mousemove', onMouseMove);
      dom.removeEventListener('mouseleave', onMouseLeave);
      cancelLeaveTimer();
    };
  }, [editor, scrollElement, clearGutter, cancelLeaveTimer]);

  // The rail/✨ is position:fixed and computed on hover, so it does not track the
  // content while scrolling. Hide it on scroll; it reappears on the next hover.
  // Prefer the explicit prop (tests); otherwise resolve the scroll ancestor from
  // the editor DOM so ref-timing on first render can't leave the listener unbound.
  useEffect(() => {
    const target =
      scrollElement ??
      editor.view.dom.closest<HTMLElement>('[data-scroll-container="editor"]');
    if (!target) return;
    const onScroll = () => { clearGutter(); };
    target.addEventListener('scroll', onScroll, { passive: true });
    return () => { target.removeEventListener('scroll', onScroll); };
  }, [editor, scrollElement, clearGutter]);

  const handleClick = useCallback(() => {
    if (posRef.current === null) return;
    const range = sectionRangeAt(editor, posRef.current);
    if (range) onGenerateChart(range);
    clearGutter();
  }, [editor, onGenerateChart, clearGutter]);

  if (gutterPos === null) return null;

  const cssVars = {
    '--chart-gutter-top': `${gutterPos.top}px`,
    '--chart-gutter-left': `${gutterPos.left}px`,
    '--chart-rail-height': `${gutterPos.height}px`,
  } as React.CSSProperties;

  return (
    <>
      {/* One continuous rail spanning the whole section. pointer-events:none so
          it is purely decorative and cannot intercept the drag handle. A single
          element → the gradient is one smooth fade top→bottom. */}
      <span className={styles.rail} style={cssVars} aria-hidden="true" />
      {/* Small fixed anchor around the button (the core-ui button forces its own
          position, so we anchor a plain element rather than fight it). The
          enter/leave flag marks "pointer over the button" so a stray null-move
          above the band can't clear it while the user is on it. */}
      <span
        className={styles.anchor}
        style={cssVars}
        onMouseEnter={() => { overButtonRef.current = true; cancelLeaveTimer(); }}
        onMouseLeave={() => {
          overButtonRef.current = false;
          // Leaving the button may not be followed by any container event (e.g.
          // pointer goes straight out of the app), so schedule a clear here too;
          // an immediate re-enter into the editor cancels it on the next move.
          cancelLeaveTimer();
          leaveTimerRef.current = setTimeout(() => {
            if (!overButtonRef.current) clearGutter();
          }, 0);
        }}
      >
        <Tooltip title="Generate chart" placement="left" mouseEnterDelay={400}>
          <IconButton
            size="sm"
            transparent
            pill
            aria-label="Generate chart from this section"
            onClick={handleClick}
          >
            <Icon name="auto_awesome" size={iconSize.sm} />
          </IconButton>
        </Tooltip>
      </span>
    </>
  );
}
